import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PublicTournamentDataService } from '../../services/publicTournamentData';

const tournamentDay = (value?: string | Date | null) => {
  if (!value) return null;
  const date = dayjs(typeof value === 'string' ? value.slice(0, 10) : value);
  return date.isValid() ? date : null;
};

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const events = useQuery({ queryKey: ['hero-upcoming-events'], queryFn: PublicTournamentDataService.getTournamentList, staleTime: 300000 });
  const stats = useQuery({ queryKey: ['platform-stats-public'], queryFn: PublicTournamentDataService.getPlatformStats, staleTime: 300000 });
  const upcoming = (events.data ?? []).filter((event) => {
    const date = tournamentDay(event.endDate ?? event.date);
    return event.id && (!date || !date.isBefore(dayjs().startOf('day')));
  }).sort((a, b) => (tournamentDay(a.date)?.valueOf() ?? Infinity) - (tournamentDay(b.date)?.valueOf() ?? Infinity)).slice(0, 3);
  const metrics = [
    { key: 'tournaments', value: stats.data?.totalTournaments },
    { key: 'teams', value: stats.data?.totalTeams },
    { key: 'matches', value: stats.data?.totalMatches },
  ];

  return (
    <div className="arena-home">
      <section className="arena-hero" aria-labelledby="arena-title">
        <div>
          <p className="arena-kicker"><span />{t('landing.eyebrow')}</p>
          <h1 id="arena-title">{t('landing.title')}<br /><em>{t('landing.titleAccent')}</em></h1>
          <p className="arena-intro">{t('landing.intro')}</p>
          <div className="arena-actions">
            <Link className="arena-button" to="/events">{t('landing.explore')}<span aria-hidden="true">↗</span></Link>
            <Link className="arena-text-link" to="/scores">{t('landing.scores')}<span aria-hidden="true">→</span></Link>
          </div>
          <p className="arena-hero__note">{t('landing.note')}</p>
        </div>
        <div className="arena-art" aria-hidden="true">
          <div className="arena-art__label"><span>Y / SPORTS</span><span>01 — PLAY</span></div>
          <svg className="arena-court" viewBox="0 0 500 600" fill="none">
            <g transform="rotate(-24 250 300)" stroke="currentColor" strokeWidth="1.5">
              <rect x="-45" y="65" width="590" height="470" /><path d="M250 65V535" /><circle cx="250" cy="300" r="77" />
              <path d="M-45 151C170 151 170 449-45 449M545 151C330 151 330 449 545 449" />
              <path d="M-45 113C223 113 223 487-45 487M545 113C277 113 277 487 545 487" strokeDasharray="7 9" />
              <circle cx="250" cy="300" r="5" fill="currentColor" />
            </g>
          </svg>
          <div className="arena-ball"><svg viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="48" /><path d="M2 50h96M50 2c-29 25-29 71 0 96M50 2c29 25 29 71 0 96M10 24c24 20 56 20 80 0M10 76c24-20 56-20 80 0" /></svg></div>
          <div className="arena-art__bottom"><span>{t('landing.artCaption')}</span><span>↗</span></div>
        </div>
      </section>
      <section className="arena-metrics" aria-label={t('landing.statsLabel')} aria-busy={stats.isLoading}>
        <p>{t('landing.community')}<span>{t('landing.communityDetail')}</span></p>
        {metrics.map(({ key, value }) => <div key={key}><strong>{stats.isLoading ? '…' : value?.toLocaleString(i18n.language) ?? '—'}</strong><span>{t(`homePlatformStats.${key}`)}</span></div>)}
        {stats.isError && <button type="button" className="arena-retry" onClick={() => void stats.refetch()}>{t('landing.retryStats')}</button>}
      </section>
      <section className="arena-events" aria-labelledby="arena-events-title">
        <div className="arena-section-head"><div><p className="arena-kicker">01 / {t('landing.calendar')}</p><h2 id="arena-events-title">{t('landing.upNext')}</h2></div><Link className="arena-text-link" to="/events">{t('landing.allEvents')}<span aria-hidden="true">↗</span></Link></div>
        <div aria-live="polite" aria-busy={events.isLoading}>
          {events.isLoading ? <div className="arena-state">{t('eventsList.loading')}</div> : events.isError ? <div className="arena-state"><h3>{t('landing.eventsError')}</h3><button className="arena-button" type="button" onClick={() => void events.refetch()}>{t('spectator.retry')}</button></div> : upcoming.length ? upcoming.map((event) => {
            const date = tournamentDay(event.date);
            return <Link className="arena-event" key={event.id} to={`/events/${event.id}`}>
              <div className="arena-event__date"><strong>{date ? date.format('DD') : '—'}</strong><span>{date ? new Intl.DateTimeFormat(i18n.language, { month: 'short', year: 'numeric' }).format(date.toDate()) : t('eventDetail.fallbacks.dateTba')}</span></div>
              <div className="arena-event__name"><span>{t('landing.tournament')}</span><h3>{event.name}</h3>{event.endDate && !dayjs(event.endDate).isSame(event.date, 'day') && <span>{[event.date, event.endDate].map(value => dayjs(value).locale(i18n.language).format('D MMM YYYY')).join(' – ')}</span>}</div>
              <p className="arena-event__location">{event.location || t('homeUpcoming.locationTba')}</p><span className="arena-event__arrow" aria-hidden="true">↗</span>
            </Link>;
          }) : <div className="arena-empty"><span className="arena-empty__symbol" aria-hidden="true">↗</span><div><h3>{t('landing.emptyTitle')}</h3><p>{t('landing.emptyCopy')}</p></div><Link className="arena-text-link" to="/events">{t('landing.pastEvents')}<span aria-hidden="true">→</span></Link></div>}
        </div>
      </section>
      <section className="arena-follow" aria-labelledby="arena-follow-title">
        <div><p className="arena-kicker">02 / {t('landing.matchday')}</p><h2 id="arena-follow-title">{t('landing.followTitle')}<br /><span>{t('landing.followAccent')}</span></h2></div>
        <div className="arena-follow__copy"><p>{t('landing.followCopy')}</p><Link className="arena-button arena-button--lime" to="/scores">{t('landing.matchCenter')}<span aria-hidden="true">↗</span></Link></div>
      </section>
    </div>
  );
}
